import { MovieCard, ReleaseYears } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import React, { FC, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { ScreenReadersOnly } from '@/shared/components/ScreenReadersOnly/ScreenReadersOnly';
import { Rating } from '@/shared/components/Rating/Rating';
import topTenImage from '@/shared/assets/top-10.png';
import { formatDurationFromSeconds } from '@/shared/lib/formatTime';
import { getTruncatedText } from '@/shared/lib/text';

import audio from '../../assets/audio.svg';
import subtitles from '../../assets/subtitles.svg';
import { I18n, useI18n } from '../../hooks/useI18n';

import { MovieTitle } from './_MovieTitle';
import styles from './MovieInfo.module.css';
import { TopTenPosition } from './_TopTenPosition';

const MAX_DESCRIPTION_LENGTH = 200;
const MAX_ANNOTATION_TEXT_EXP_LENGTH = 84;
const MAX_GENRES_LENGTH = 2;

interface MovieInfoProps {
    movie: MovieCard;
}

function getTvSeriesYears(years: ReleaseYears | null, i18n: I18n): string | undefined {
    const { start, end } = years || {};

    if (!start) {
        return undefined;
    }

    if (start === end) {
        return String(start);
    }

    return i18n('movie_info__start_year_of_series', { start });
}

export const MovieInfo: FC<MovieInfoProps> = ({ movie }) => {
    const intl = useIntl();
    const i18n = useI18n();

    const { kpRating, top10Position } = movie.ratings;
    const isInTop10 = Boolean(top10Position);

    const meta = useMemo(() => {
        const years = movie.productionYear ?? getTvSeriesYears(movie.releaseYears, i18n);
        const genres = movie.genres.slice(0, MAX_GENRES_LENGTH).join(', ');
        const seasons = movie.seasonsNumber
            ? i18n('movie_info__seasons_quantity', { seasonsQuantity: movie.seasonsNumber })
            : '';
        const filmDuration = movie.filmDuration
            ? formatDurationFromSeconds(intl, movie.filmDuration)
            : '';
        const restrictionAge = movie.restriction?.toString();

        return [
            { text: [years, genres].filter(Boolean).join(', ') },
            { text: seasons },
            { text: movie.countries?.[0] || '' },
            { text: filmDuration },
            { text: restrictionAge, dir: 'ltr' },
        ].filter(({ text }) => Boolean(text));
    }, [movie, intl, i18n]);

    const hasMovieFeatures =
        movie.features?.voiceFeatures ||
        movie.features?.subtitlesFeatures ||
        movie.features?.qualityFeatures;

    const description = movie.shortDescription
        ? getTruncatedText(movie.shortDescription, MAX_DESCRIPTION_LENGTH)
        : undefined;
    const annotation = movie.editorAnnotation.slice(0, MAX_ANNOTATION_TEXT_EXP_LENGTH);

    return (
        <div className={styles.root}>
            <MovieTitle
                logo={movie.horizontalLogo?.toString()}
                title={movie.title.toString()}
                alt={movie.title.toString()}
            />
            <div
                className={classnames(styles.baseMeta, {
                    [styles.baseMetaColumn]: Boolean(kpRating) && isInTop10,
                })}
            >
                <div className={styles.rating}>
                    {kpRating && (
                        <Rating
                            className={styles.kpRating}
                            value={kpRating?.value}
                            votesCount={kpRating?.count}
                            size={isInTop10 ? 'large' : 'default'}
                        />
                    )}
                    {top10Position && (
                        <div className={styles.top10}>
                            <img
                                className={styles.top10Badge}
                                src={topTenImage}
                                alt=""
                                width={21}
                                height={24}
                            />
                            <div className={styles.top10Title}>{i18n('movie_info__top10')}</div>
                            <TopTenPosition position={top10Position} />
                            <ScreenReadersOnly>
                                {i18n('movies_info__top10_screen_reader_position', {
                                    position: top10Position,
                                })}
                            </ScreenReadersOnly>
                        </div>
                    )}
                </div>
                <div className={styles.metaContent}>
                    <ul className={styles.meta}>
                        {meta.map(({ text, dir }) => (
                            <li key={text} className={styles.metaItem} dir={dir}>
                                {text}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            {hasMovieFeatures && (
                <div className={styles.movieFeatures}>
                    {movie.features?.qualityFeatures &&
                        movie.features?.qualityFeatures.length > 0 && (
                            <div className={styles.features}>
                                {movie.features?.qualityFeatures.map((feature) => (
                                    <div
                                        key={feature.title}
                                        className={classnames(styles.qualityFeature, {
                                            [styles.qualityFeatureWithFilled]:
                                                feature.title === '4K',
                                        })}
                                        aria-label={feature.title}
                                    >
                                        {feature.title}
                                    </div>
                                ))}
                            </div>
                        )}
                    {movie.features?.voiceFeatures && movie.features?.voiceFeatures.length > 0 && (
                        <div className={styles.features}>
                            <img
                                src={audio}
                                className={styles.icon}
                                alt={i18n('movie_info__audio_aria_label')}
                                role="presentation"
                                data-tid="Audio"
                            />
                            {movie.features?.voiceFeatures.join(', ')}
                        </div>
                    )}
                    {movie.features?.subtitlesFeatures &&
                        movie.features?.subtitlesFeatures.length > 0 && (
                            <div className={styles.features}>
                                <img
                                    src={subtitles}
                                    className={styles.icon}
                                    alt={i18n('movie_info__subtitles_aria_label')}
                                    role="presentation"
                                    data-tid="Subtitles"
                                />
                                {movie.features?.subtitlesFeatures.join(', ')}
                            </div>
                        )}
                </div>
            )}
            {annotation && <div className={styles.annotation}>{annotation}</div>}
            {description && <div className={styles.description}>{description}</div>}
        </div>
    );
};
